import {
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Theme,
  useTheme,
} from "@material-ui/core";
import { AssignmentInd, Minimize, Visibility } from "@material-ui/icons";
import Close from "@material-ui/icons/Close";
import { AxiosPromise } from "axios";
import { useEffect } from "react";
import { useContext, useState } from "react";
import { Button } from "react-admin";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import { handleAxiosError } from "../../../common/data/axiosErrorHandler";
import resourceNames from "../../../common/data/resourceNames";
import {
  DvirFormAreaPointSeverity,
  Int32CommonDto,
  SafetyDashboardApiFactory,
} from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { CustomResourceRouteParams } from "../../../common/types";
import { useButtonStyles } from "../../assets/maintenance-dashboard/common";
import { SelectedDriversFilterContext } from "./common";
import styles from "./SelectedDriversList.module.css";

interface Props {
  handleSidebar: (isVisible: boolean) => void;
}

export const SelectedDriversList = (props: Props) => {
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(true);
  const filterContext = useContext(SelectedDriversFilterContext);
  const [data, setData] = useState<Int32CommonDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const axios = useAxiosInstance();
  const buttonClasses = useButtonStyles();
  const history = useHistory();
  const params = useParams<CustomResourceRouteParams>();
  const vehicleId = params.id;
  const theme = useTheme<Theme>();
  const { handleSidebar } = props;

  useEffect(() => {
    handleSidebar(sidebarVisible);
  }, [sidebarVisible, handleSidebar]);

  useEffect(() => {
    if (!axios) {
      return;
    }

    if (!filterContext) {
      return;
    }

    if (!filterContext.selectedFilter) {
      return;
    }

    let func:
      | ((options?: any) => AxiosPromise<Int32CommonDto[]>)
      | undefined = undefined;
    const factory = SafetyDashboardApiFactory(
      undefined,
      axios.defaults.baseURL,
      axios
    );
    switch (filterContext.selectedFilter) {
      case "WITH_UNRESOLVED_DVIR_DEFECTS":
        func = () =>
          factory.apiAppSafetydashboardDriverswithunresolveddvirdefectsGet(
            DvirFormAreaPointSeverity.None
          );
        break;
      case "WITH_MAJOR_UNRESOLVED_DVIR_DEFECTS":
        func = () =>
          factory.apiAppSafetydashboardDriverswithunresolveddvirdefectsGet(
            DvirFormAreaPointSeverity.Major
          );
        break;
      case "WITH_MINOR_UNRESOLVED_DVIR_DEFECTS":
        func = () =>
          factory.apiAppSafetydashboardDriverswithunresolveddvirdefectsGet(
            DvirFormAreaPointSeverity.Minor
          );
        break;
      case "WITH_HARSH_EVENTS":
        func = factory.apiAppSafetydashboardDriverswithgeometrisalertsGet;
        break;
      case "WITH_SPEEDING_EVENTS":
        func = factory.apiAppSafetydashboardDriverswithspeedingeventsGet;
        break;
      case "WITH_PHYSICAL_ELD_DISCONNECTIONS":
        func =
          factory.apiAppSafetydashboardDriverswithphysicalelddisconnectionsGet;
        break;
      case "WITH_HARSH_ACCELERATION":
        func =
          factory.apiAppSafetydashboardDriverswithharshaccelerationeventsGet;
        break;
      case "WITH_HARSH_BRAKING":
        func = factory.apiAppSafetydashboardDriverswithharshbrakingeventsGet;
        break;
      case "WITH_HARSH_TURNS":
        func = factory.apiAppSafetydashboardDriverswithharshturneventsGet;
        break;
      case "WITH_IMPACTS":
        func = factory.apiAppSafetydashboardDriverswithimpacteventsGet;
        break;
    }

    if (!func) {
      return;
    }

    setLoading(true);
    func()
      .then((response) => {
        setData(response.data);
      })
      .catch((e) => console.error(handleAxiosError(e)))
      .finally(() => setLoading(false));
  }, [axios, filterContext]);

  return loading ? (
    <CircularProgress size={12} />
  ) : (
    <div className={styles["selected-drivers-list-wrapper"]}>
      <div
        className={styles["title-bar"]}
        style={{ background: theme.palette.background.paper }}>
        <IconButton onClick={() => setSidebarVisible((x) => !x)}>
          {sidebarVisible ? <Minimize /> : <Visibility />}
        </IconButton>

        <IconButton
          onClick={() =>
            filterContext && filterContext.setSelectedFilter(undefined)
          }>
          <Close />
        </IconButton>
        <Divider />
      </div>
      {sidebarVisible ? (
        <div
          className={styles["selected-drivers-list"]}
          style={{ background: theme.palette.background.paper }}>
          <List dense>
            {data.map((x) => (
              <ListItem key={x.id}>
                <Button
                  label={x.name!}
                  className={buttonClasses.label}
                  style={
                    vehicleId === `${x.id}`
                      ? { fontWeight: 700, color: theme.palette.info.light }
                      : {}
                  }
                  onClick={() =>
                    history.push(`/${resourceNames.SAFETY_DASHBOARD}/${x.id}`)
                  }>
                  <AssignmentInd />
                </Button>
              </ListItem>
            ))}
          </List>
        </div>
      ) : null}
      <div
        className={`${
          sidebarVisible
            ? styles["selected-drivers-list-footer"]
            : styles["selected-drivers-list-footer-closed"]
        }`}
        style={{ background: theme.palette.background.paper }}>
        &nbsp;
      </div>
    </div>
  );
};
